import React, { useState, useEffect, useRef } from 'react';

import { Modal } from 'flowbite';

import UsersCreate from './UsersCreate';
import UsersTableRow from './UsersTableRow';

import { modalOptions } from 'Helpers/modalOptions';
import { combinePaginationUrl } from 'Helpers/urlService';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

// Embedded in another page's panel (the set-up wizard): no card or heading of
// its own, and compact white rows with top-aligned cells. The panel is narrower
// than the table's natural width once the app sidebar is open, so headers and
// cell text wrap instead of forcing a sideways scroll.
const EMBEDDED_TABLE_CLASSES = [
    'tw-w-full',
    '[&_th]:!tw-text-start',
    '[&_th]:!tw-align-middle',
    '[&_th]:!tw-text-[11px]',
    '[&_th]:!tw-px-1',
    '[&_th]:!tw-py-3',
    '[&_tbody_tr]:!tw-bg-white',
    '[&_td]:!tw-align-top',
    '[&_td]:!tw-text-[11px]',
    '[&_td]:!tw-px-1',
    '[&_td]:!tw-py-3',
    '[&_td]:!tw-border-r-0',
    '[&_td]:!tw-break-words',
    '[&_td.users-email-cell]:![overflow-wrap:anywhere]',
    // The Modules list is the longest cell; capping it leaves the other columns
    // wide enough to wrap at word boundaries.
    '[&_td.users-modules-cell]:!tw-w-[7.5rem]',
    '[&_td_.users-actions]:!tw-flex-col',
    '[&_td_.users-actions]:!tw-items-start',
    '[&_td_.users-actions]:!tw-gap-y-1',
].join(' ');

function UsersTable(props) {
    const embedded = !!props.embedded;
    const [roles, setRoles] = useState(props.roles);
    const [users, setUsers] = useState([]);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [to, setTo] = useState(1);
    const [from, setFrom] = useState(1);
    const [total, setTotal] = useState(1);
    const [deletedUser, setDeletedUser] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [usersCreateModalElement, setUsersCreateModalElement] = useState(null);
    const [usersDeleteModalElement, setUsersDeleteModalElement] = useState(null);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const usersCreateModalRef = useRef();
    const usersDeleteModalRef = useRef();

    useEffect(() => {
        fetchUsers();
        if (props.canCreate) {
            usersCreateModalRef && setUsersCreateModalElement(new Modal(usersCreateModalRef.current, modalOptions(false)));
        }
        if (props.canDelete) {
            usersDeleteModalRef && setUsersDeleteModalElement(new Modal(usersDeleteModalRef.current, modalOptions(false)));
        }
    }, []);

    useEffect(() => {
        if (deletedUser) {
            usersDeleteModalElement && usersDeleteModalElement.show();
        }
    }, [deletedUser]);

    const defaultUrl = combinePaginationUrl(props.fetchUsersUrl, perPage);

    const combineFetchUrl = (p = null, pp = null) => {
        return combinePaginationUrl(props.fetchUsersUrl, pp || perPage, p || page);
    };

    const confirmDeletedUser = (user) => {
        setDeletedUser(user);
    };

    const clearDeletedUser = () => {
        setDeletedUser(null);
        usersDeleteModalElement && usersDeleteModalElement.hide();
    };

    const successDeletedHandler = () => {
        setShowToast(true);
        $('#user-deleted-toast').toast('show');
        setTimeout(() => {
            setShowToast(false);
        }, 3000);
    };

    const fetchUsers = (url = null) => {
        const fetchUrl = url || defaultUrl;

        axios
            .get(fetchUrl)
            .then((res) => {
                const { data, to, from, total, last_page } = res.data;

                const UserData = [];
                data.forEach((item) => {
                    item.general_rev_departments.forEach((revDep) => {
                        const obj = JSON.parse(JSON.stringify(revDep));
                        obj.id = 'general_' + obj.id;
                        item.revdepartments.unshift(obj);
                    });
                    UserData.push(item);
                });

                setUsers(UserData);
                setTo(to);
                setFrom(from);
                setTotal(total);
                setLastPage(last_page);
            })
            .catch((error) => {
                console.log(error);
            });
        props.fetchOrganisationUsers();
    };

    function pageChange(data) {
        const url = combineFetchUrl(data.selected + 1);
        setPage(+data.selected + 1);
        fetchUsers(url);
    }

    const getUpdateUrl = (id) => {
        return props.updateUserUrl.replace('user_id', id);
    };

    const triggerCreateUserModal = () => {
        usersCreateModalElement && usersCreateModalElement.show();
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const triggerDeleteUser = () => {
        if (!!props.canDelete == false) {
            return;
        }
        setActionInProgress(true);
        axios
            .delete(props.deleteUserUrl.replace('user_id', deletedUser.id))
            .then((response) => {
                successDeletedHandler();
                fetchUsers();
                setActionCompleted(true);
                clearDeletedUser();
            })
            .catch((e) => {
                setActionInProgress(false);
                alert(e.response.data.error);
                console.log(e.response);
            });
    };

    return (
        <>
            <div id="user-deleted-toast" className={'toast ' + !showToast ? 'hidden' : ''} style={{ position: 'absolute', top: 0, right: 0 }} data-delay={2000}>
                <div className="toast-header">
                    <strong className="mr-auto">{$t('User deleted')}</strong>
                    <button type="button" className="ml-2 mb-1 close" data-dismiss="toast" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div className="toast-body">{$t('User was deleted successfully')}</div>
            </div>

            <div className={embedded ? undefined : 'table__card'}>
                <div className={embedded ? 'tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mb-4' : 'tw-flex tw-items-start tw-justify-between tw-mb-6'}>
                    <div>
                        {!embedded && <h4 className="table__header">{$t('Users')}</h4>}
                        <div className="tw-flex tw-items-center tw-space-x-3">
                            <PaginationInfo to={to} from={from} total={total} />
                            <TablePagination lastPage={lastPage} pageChange={pageChange} />
                        </div>
                    </div>
                    {!!props.canCreate && (
                        <Button variant="primary" onClick={() => triggerCreateUserModal()}>
                            {embedded ? $t('Add new user') : $t('Add New User')}
                        </Button>
                    )}
                </div>

                <div className="inbud-table_wrapper">
                    <table className={embedded ? `inbud-table ${EMBEDDED_TABLE_CLASSES}` : 'inbud-table'}>
                        <thead>
                            <tr>
                                <th>{$t('Name')}</th>
                                <th>{$t('Email')}</th>
                                <th>{$t('Roles')}</th>
                                <th>{$t('Departments')}</th>
                                <th>{$t('Venues')}</th>
                                <th>{embedded ? $t('Cost centres') : $t('Cost Centers')}</th>
                                <th>{embedded ? $t('Rev departments') : $t('Rev Departments')}</th>
                                <th>{embedded ? $t('Rev cost centres') : $t('Rev Cost Centers')}</th>
                                <th>{$t('Modules')}</th>
                                <th>{$t('Active')}</th>
                                <th>{$t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => {
                                return (
                                    <UsersTableRow
                                        key={user.id}
                                        user={user}
                                        roles={roles}
                                        departments={props.departments}
                                        costcenters={props.costcenters}
                                        revDepartments={props.revDepartments}
                                        revCostcenters={props.revCostcenters}
                                        venues={props.venues}
                                        superAdminRole={props.superAdminRole}
                                        updateUserUrl={getUpdateUrl(user.id)}
                                        confirmDeleteHandler={confirmDeletedUser}
                                        canEdit={!!props.canEdit}
                                        canDelete={!!props.canDelete}
                                        canEnable={props.canEnable && parseInt(user.id) !== parseInt(props.authId)}
                                        modules={props.modules}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {!!props.canCreate && (
                <UsersCreate
                    modalId="create-user"
                    modalRef={usersCreateModalRef}
                    modalElement={usersCreateModalElement}
                    roles={roles}
                    departments={props.departments}
                    costcenters={props.costcenters}
                    revDepartments={props.revDepartments}
                    revCostcenters={props.revCostcenters}
                    venues={props.venues}
                    fetchUsersHandler={fetchUsers}
                    storeUserUrl={props.storeUserUrl}
                    canCreate={props.canCreate}
                    organisationUsers={props.organisationUsers}
                    modules={props.modules}
                />
            )}

            {!!props.canDelete && (
                <ConfirmationModal
                    successHandler={successDeletedHandler}
                    closeHandler={clearDeletedUser}
                    triggerAction={triggerDeleteUser}
                    cleanStates={cleanConfirmationModalStates}
                    allowAction={deletedUser != undefined}
                    modalId={'delete-user'}
                    modalRef={usersDeleteModalRef}
                    modalElement={usersDeleteModalElement}
                    request={actionInProgress}
                    actionCompleted={actionCompleted}
                    actionFailed={actionFailed}
                    confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: deletedUser?.first_name + ' ' + deletedUser?.last_name })}
                    actionCompletedMsg=""
                    actionFailedMsg=""
                    title={$t('Delete User')}
                    cancelButtonText={$t('Close')}
                    primaryButtonText={$t('Delete')}
                />
            )}
        </>
    );
}

export default UsersTable;
